import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Button, 
  FormControl, 
  TextField, 
  Typography, 
  Select, 
  MenuItem, 
  InputLabel,
  Paper,
  Box,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { addTransfer } from '../../redux/transactionSlice';
import { loadAccount } from '../../redux/financeAccountSlice';
import { loadCategory } from '../../redux/categorySlice';
import { dateToString } from '../../shared/formatters';

const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: '800px',
  margin: 0,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  borderRadius: '20px',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: '16px',
    margin: 0,
  },
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    '&.Mui-focused': {
      color: 'rgb(0,98,105)',
      fontWeight: 600,
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: '#fafafa',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgb(0,98,105)',
  },
  '&.Mui-focused': {
    backgroundColor: '#ffffff',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgb(0,98,105)',
    borderWidth: '2px',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: 'rgb(0,98,105)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgb(0,85,91)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
  },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: '#1a1a1a',
  marginBottom: theme.spacing(4),
  textAlign: 'left',
  position: 'relative',
  paddingBottom: theme.spacing(2),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '4px',
    backgroundColor: 'rgb(0,98,105)',
    borderRadius: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
    marginBottom: theme.spacing(3),
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const fillItem = () => {
  let item = { id: 0, description: '', value: 0, financeAccountOriginId: 0, financeAccountDestinationId: 0, date: dateToString(new Date()) };
  return item;
}

const goToList = (navigate) => {
  navigate('/transactions');
};

const Transfer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const addTransferAction = async (item) => { await dispatch(addTransfer(item)).unwrap() };

  const submitForm = async (event) => {
    event.preventDefault();
    await addTransferAction (item);
    navigate('/transactions');
  };

  // Redux state selectors
  const accounts = useSelector(state => state.financeAccount.items);
  const categories = useSelector(state => state.category.items);
  
  // Redux actions
  if (!accounts || accounts.length === 0) {
    dispatch(loadAccount());
  }

  if (!categories || categories.length === 0) {
    dispatch(loadCategory());
  }  
  
  let initialData = fillItem();

  const [item, updateItem] = useState({ ...initialData });

  if (categories.length > 0) {
    item.creditTransferCategoryId = categories.find(category => category.type === 'credit-transfer').id;
    item.debitTransferCategoryId = categories.find(category => category.type === 'debit-transfer').id;
  }

  return (
    <Box sx={{ 
      padding: { xs: 0.5, sm: 1 },
      minHeight: '100vh',
    }}>
      <FormContainer elevation={0}>
        <PageTitle component="h1" variant="h4">
          Transferência entre Contas
        </PageTitle>

        <StyledForm onSubmit={event => submitForm(event)}>
          <FormSection>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <StyledTextField
                id="value"
                name="value"
                label="Valor"
                type="number"
                inputProps={{ step: "0.01" }}
                fullWidth
                autoFocus
                placeholder="0,00"
                value={item.value}
                onChange={event => updateItem({ ...item, value: event.target.value })}
              />
              
              <StyledTextField
                id="date"
                name="date"
                label="Data"
                type="date"
                fullWidth
                value={item.date}
                onChange={event => updateItem({ ...item, date: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <StyledTextField
              id="description"
              name="description"
              label="Descrição"
              fullWidth
              placeholder="Ex: Transferência para poupança, Retirada para despesas..."
              value={item.description}
              onChange={event => updateItem({ ...item, description: event.target.value })}
            />
          </FormSection>

          <SectionTitle>Contas</SectionTitle>

          <FormSection>
            <StyledFormControl>
              <InputLabel>Conta Origem</InputLabel>
              <StyledSelect
                value={item.financeAccountOriginId}
                label="Conta Origem"
                onChange={event => updateItem({ ...item, financeAccountOriginId: event.target.value })}
              >
                {(accounts || []).map(account => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
            
            <StyledFormControl>
              <InputLabel>Conta Destino</InputLabel>
              <StyledSelect
                value={item.financeAccountDestinationId}
                label="Conta Destino"
                onChange={event => updateItem({ ...item, financeAccountDestinationId: event.target.value })}
              >
                {(accounts || []).map(account => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </StyledFormControl>
          </FormSection>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            sx={{ pt: 3, mt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}
          >
            <PrimaryButton
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ flex: 1 }}
            >
              Salvar
            </PrimaryButton>
            
            <SecondaryButton
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => goToList(navigate)}
            >
              Voltar
            </SecondaryButton>
          </Stack>
        </StyledForm>
      </FormContainer>
    </Box>
  );
};

export default Transfer;
